import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo } from 'react';
import CurrenciesListRaw from '../../components/CurrenciesListRaw/CurrenciesListRaw';
import { CURRENCIES_SORT_COLUMN } from '../../types/currencies';
import { SORTING_DIRECTION } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import { LoadingStatus } from 'src/types/main';
import { emptyArray } from 'src/utils/helpers';
import ColumnTitle from 'src/view/components/ColumnTitle/ColumnTitle';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import currenciesStore from 'src/view/screens/SettingsScreen/store/currenciesStore';
import Pagination from '~/view/components/Controls/Pagination';

const CurrenciesList: React.FC = observer((): JSX.Element => {
  useEffect(() => {
    currenciesStore.getCurrencies();
  }, []);

  const loadingStatuses = [LoadingStatus.Loading, LoadingStatus.Init, LoadingStatus.Refreshing];

  const setFilters = useCallback((sortField: CURRENCIES_SORT_COLUMN | undefined, sortDir: SORTING_DIRECTION | undefined) => {
    currenciesStore.setFilters({ sortField, sortDir });
  }, []);

  const noFoundText: string = localeStore.t(currenciesStore.filters.search.length ? 'no_results_found' : 'no_items_yet');

  const isLoading = useMemo(() => {
    return loadingStatuses.includes(currenciesStore.currenciesLoadingStatus);
  }, [currenciesStore.currenciesLoadingStatus]);

  const handleClickPagination = useCallback((pageNumber: number) => {
    currenciesStore.changePage(pageNumber);
  }, []);

  return (
    <div className="d-flex flex-column flex-grow-1 pt-4">
      {!currenciesStore.currencies.length && !loadingStatuses.includes(currenciesStore.currenciesLoadingStatus) ? (
        <div className="d-flex flex-column justify-content-center align-items-center mt-2 h-100">
          <p className="family-medium text-size-24 color-gray2">{noFoundText}</p>
        </div>
      ) : (
        <>
          <div className="d-flex flex-row mb-4 pr-5 w-100">
            <ColumnTitle<CURRENCIES_SORT_COLUMN>
              title="name"
              columnName={CURRENCIES_SORT_COLUMN.NAME}
              currentField={currenciesStore.filters.sortField}
              currentDir={currenciesStore.filters.sortDir}
              setFilters={setFilters}
              className="w-20 pl-2"
            />
            <ColumnTitle<CURRENCIES_SORT_COLUMN>
              title="symbol"
              columnName={CURRENCIES_SORT_COLUMN.SYMBOL}
              currentField={currenciesStore.filters.sortField}
              currentDir={currenciesStore.filters.sortDir}
              setFilters={setFilters}
              className="w-17"
            />
            <ColumnTitle<CURRENCIES_SORT_COLUMN>
              title="code"
              columnName={CURRENCIES_SORT_COLUMN.CODE}
              currentField={currenciesStore.filters.sortField}
              currentDir={currenciesStore.filters.sortDir}
              setFilters={setFilters}
              className="w-15"
            />
            <ColumnTitle<CURRENCIES_SORT_COLUMN>
              title="code_ar"
              columnName={CURRENCIES_SORT_COLUMN.CORE_AR}
              currentField={currenciesStore.filters.sortField}
              currentDir={currenciesStore.filters.sortDir}
              setFilters={setFilters}
              className="w-15"
            />
            <ColumnTitle<CURRENCIES_SORT_COLUMN>
              title="multiplier"
              columnName={CURRENCIES_SORT_COLUMN.MULTIPLIER}
              currentField={currenciesStore.filters.sortField}
              currentDir={currenciesStore.filters.sortDir}
              setFilters={setFilters}
              className="w-14"
            />
            <ColumnTitle title="status" className="w-19" />
          </div>
          <ScrollableBlock showScrollbar scrollClassName="d-flex flex-column full-height pr-4" className="mb-3">
            {isLoading
              ? emptyArray().map((_, index) => <CurrenciesListRaw key={'key-' + String(index)} index={index} />)
              : currenciesStore.currencies.map((item, index) => <CurrenciesListRaw key={item.id} index={index} currency={item} />)}
          </ScrollableBlock>
        </>
      )}
      <Pagination
        visible={currenciesStore.currencies.length > 0 && currenciesStore.pagination.lastPage > 1}
        className="mt-2"
        pagination={currenciesStore.pagination}
        onChange={handleClickPagination}
      />
    </div>
  );
});

export default CurrenciesList;
